import { Company, DataInstance, Incident, TopicBuilder, Units, Users } from "@busy-human/amt-library";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAuth } from "./useAuth";
import { useUsers } from "./useUsers";
import { onSnapshot } from 'firebase/firestore'
import { ISOTimestamp } from "@busy-human/gearbox";
import { queueIncidentReport, updateIncidentStatus } from "@/util/functions";
import { toast } from "@/util/toastHelper";
import { NotificationHandled, useMessaging } from "@/util/messaging";
import { markMessageRead, useNotificationDB } from "@/util/msgdb";
import { events } from "@/events";

type IncidentChange = {
	status: Units.UnitStatus
	unit?: string
	userId: string
	/** Timestamp */
	start: number | string
	/** Timestamp */
	end?: number | string
	turndownReason?: string
	reason?: string
	position?: "coordinates"
	companyNotified?: boolean

	incidentId: string
	logIdx: number
	incidentNumber: string

}

type userAck = {
	name: string
	incidentNumber: string
	timestamp: string
}

export const useIncidents = defineStore('incidents', () => {
	const auth = useAuth();
	const users = useUsers();
	const incidents = ref(new Map<string, DataInstance<Incident.Incident>>());
	/** Maps unit Ids to incident Ids */
	const unitIncident = ref(new Map<string, string>());
	const incidentChanges = ref(new Map<string, IncidentChange>());
	const unitUserAcks = ref<userAck[]>([]);
	const loaded = ref(false);
	const unsub = ref<(() => void) | null>(null)

	const messaging = useMessaging();

	const waitingPromises = ref<(() => void)[]>([])

	function getCollection() {
		const auth = useAuth();
		if (!auth.companyId) throw new Error("No companyId!");
		return Company.Companies.subCollectionEntity(auth.companyId, 'incidents')
	}

	function getCompanyId() {
		const auth = useAuth();
		if (!auth.companyId) throw new Error("No companyId!");
		return auth.companyId;
	}

	const referenceTime = ref(new Date().getTime());

	watch(() => auth.companyId, async (companyId, prevCompanyId) => {
		loaded.value = false;
		if (unsub.value) unsub.value();
		unsub.value = null;

		incidents.value.clear();
		unitIncident.value.clear();
		incidentChanges.value.clear();

		if (companyId) {
			const collection = Company.Companies.subCollectionEntity(companyId, 'incidents')
			const subset = collection.all();

			unsub.value = onSnapshot(subset.ref(), next => {
				for (const docChange of next.docChanges()) {

					const incident: DataInstance<Incident.Incident> = {
						$id: docChange.doc.id,
						...docChange.doc.data() as Incident.Incident
					}

					const isAmtIncident = !!incident.amtInfo;
					let shouldClearAmtNotifications = false;

					if (incident.statusLog) {
						const users = useUsers();
						const currentUserId = users.currentUser?.$id

						incident.statusLog?.forEach((status, idx) => {
							if (new Date(status.start).getTime() < referenceTime.value) return;

							if (!status.companyNotified && (currentUserId && status.userId !== currentUserId)) {
								let incidentChange = incidentChanges.value.get(incident.$id)

								if (!incidentChange || incidentChange.logIdx !== idx || incidentChange.start !== status.start) {
									let newLog: IncidentChange = {
										...status,
										incidentId: incident.$id,
										incidentNumber: incident.number,
										logIdx: idx
									}

									incidentChanges.value.set(incident.$id, newLog)
								}
							}
						})
					}

					// unit ack stuff
					const before = incidents.value.get(incident.$id);
					if (before?.unitAck && incident.unitAck) {
						for (const [userId, ack] of Object.entries(before.unitAck)) {
							if (!ack && userId in incident.unitAck && incident.unitAck[userId]) {
								const user = users.users.get(userId);
								unitUserAcks.value.push({
									name: Users.resolveUserName(user),
									incidentNumber: incident.number,
									timestamp: new Date().toISOString()
								})
							} else {

							}
						}
					}

					if (!!incident.amtInfo && !!before?.amtInfo && incident.amtInfo.responded && !before.amtInfo.responded) {
						shouldClearAmtNotifications = true;
					}

					if (docChange.type === 'removed') {
						if (incident.assignedUnit && unitIncident.value.get(incident.assignedUnit) === incident.$id) {
							unitIncident.value.delete(incident.assignedUnit);
						}
						incidents.value.delete(docChange.doc.id);
						if (isAmtIncident) {
							shouldClearAmtNotifications = true;
						}
					} else {
						incidents.value.set(incident.$id, incident);
						if (incident.assignedUnit && incident.active) {
							unitIncident.value.set(incident.assignedUnit, incident.$id);
						} else if (incident.assignedUnit && !incident.active && unitIncident.value.get(incident.assignedUnit) === incident.$id) {
							unitIncident.value.delete(incident.assignedUnit);
						}

						//Remove unitIncidents referencing incident if there is none assigned
						if (!incident.assignedUnit && [...unitIncident.value.values()].includes(incident.$id)) {
							[...unitIncident.value.keys()].forEach((unitId) => {
								if (unitIncident.value.get(unitId) === incident.$id) {
									unitIncident.value.delete(unitId);
								}
							})
						}
					}

					if (shouldClearAmtNotifications) {
						const msgdb = useNotificationDB();
						for (const n of msgdb.unread) {
							if (n.data?.type === 'amt-incident' && n.data.incidentId === incident.$id) {
								markMessageRead(n.messageId);
							}
						}
					}
				}
				loaded.value = true;
				waitingPromises.value.forEach(res => res());
				waitingPromises.value.length = 0;
			})
		}

		if (prevCompanyId && companyId !== prevCompanyId) {
			await messaging.removeTopicHandler(TopicBuilder.IncidentStatus(prevCompanyId));
			await messaging.removeTopicHandler(TopicBuilder.AMTIncident(prevCompanyId));
			await messaging.removeTopicHandler(TopicBuilder.IncidentAck(prevCompanyId));
		}

		if (companyId && companyId !== prevCompanyId) {
			await messaging.addTopicHandler<{ triggeredBy: string, unitId: string }>(TopicBuilder.IncidentStatus(companyId), {
				onReceived: (data) => data.triggeredBy === auth.currentUID ? NotificationHandled.Ignored : undefined
			});
			await messaging.addTopicHandler(TopicBuilder.IncidentAck(companyId));
			await messaging.addTopicHandler<{ incidentId: string }>(TopicBuilder.AMTIncident(companyId), {
				disableAutoclear: true,
				onClicked: async ({ incidentId }, msg) => {
					const incident = incidents.value.get(incidentId);
					if (!incident) {
						toast.simple("AMT Incident not found");
						await markMessageRead(msg.messageId);
						return;
					}
					if (incident.amtInfo?.responded || incident.assignedUnit) {
						toast.simple("AMT Incident already responded to");
						await markMessageRead(msg.messageId);
						return;
					}

					events.emit('AMTNOTE:OPENED', incidentId);
					await markMessageRead(msg.messageId);
				}
			});
		}
	}, { immediate: true })

	function waitForReady() {
		if (!loaded.value) return new Promise<void>(res => waitingPromises.value.push(res))
		else return Promise.resolve()
	}

	async function updateIncident(incidentId: string, incidentDetails: Partial<Incident.Incident>) {
		if (!auth.companyId) throw new Error("no companyId!");

		const collection = Company.Companies.subCollectionEntity(auth.companyId, 'incidents');
		await collection.updateDoc(incidentId, incidentDetails)
	}

	async function declineIncident(incidentId: string, turndownReason: string) {
		// Handle local data
		const incident = incidents.value.get(incidentId);
		if (incident) {
			const prevId = incident.assignedUnit;
			if (prevId) unitIncident.value.delete(prevId);
		}

		// Update status
		const { data: res } = await updateIncidentStatus({
			companyId: getCompanyId(),
			incidentId,
			status: Units.UnitStatus.Declined,
			turndownReason,
			unitId: incident?.assignedUnit ?? undefined,
			changedAt: new Date().toISOString(),
			wasOffline: false,
			updateReqId: crypto.randomUUID(),
			source: `WEB ${VERSION}`
		});

		if (res === "DUPLICATE_REQUEST") {
			toast.simple("Duplicate status update was ignored");
		}

		// Unassign unit
		await updateIncident(incidentId, { assignedUnit: null });
	}

	async function updateStatus(incidentId: string, status: Units.UnitStatus, unitId?: string, turndownReason?: string, rescheduleTime?: ISOTimestamp) {

		// Local data
		const collection = getCollection();
		const incident = await collection.fetchData(incidentId).catch(() => null);
		if (incident) {
			const prevId = incident.assignedUnit;
			if (prevId !== unitId && prevId) unitIncident.value.delete(prevId);
		}

		// Update status
		const { data: res } = await updateIncidentStatus({
			companyId: getCompanyId(),
			incidentId, status, unitId, turndownReason,
			changedAt: new Date().toISOString(),
			wasOffline: false,
			updateReqId: crypto.randomUUID(),
			source: `WEB ${VERSION}`
		});

		if (res === "DUPLICATE_REQUEST") {
			toast.simple("Duplicate status update was ignored");
		}

		// Unit assignment, or reschedule time
		if (incident?.assignedUnit !== unitId || rescheduleTime) {
			const tmp: Partial<Incident.Incident> = {};
			if (incident?.assignedUnit !== unitId) tmp.assignedUnit = unitId;
			if (rescheduleTime) tmp.scheduledTime = rescheduleTime;
			await updateIncident(incidentId, tmp)
		}
	}

	async function reopenIncident(incidentId: string) {
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`No such incident: ${incidentId}`);
		incident.assignedUnitOverride = null;

		// Update status
		const { data: res } = await updateIncidentStatus({
			companyId: getCompanyId(),
			incidentId,
			status: Units.UnitStatus.Pending,
			changedAt: new Date().toISOString(),
			wasOffline: false,
			updateReqId: crypto.randomUUID(),
			source: `WEB ${VERSION}`
		});

		if (res === "DUPLICATE_REQUEST") {
			toast.simple("Duplicate status update was ignored");
		}

		// Update other details
		await updateIncident(incidentId, {
			active: true,
			assignedUnit: null,
			assignedUnitOverride: null
		});
	}

	type UnitSnapshot = {
		pilotId?: string | null,
		sicId?: string | null,
		med1Id?: string | null,
		med2Id?: string | null,
		mechId?: string | null,
		tailNumber?: string
		createdBy: string
	}

	async function overrideIncidentUnit(incidentId: string, snapshot: UnitSnapshot) {
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`No such incident: ${incidentId}`);

		snapshot.createdBy = auth.currentUID!;
		incident.assignedUnitOverride = snapshot;

		const collection = getCollection();
		await collection.updateDoc(incidentId, {
			assignedUnitOverride: snapshot
		})
	}

	async function setLoadedMiles(incidentId: string, loadedMiles: number) {
		const collection = getCollection();
		await collection.updateDoc(incidentId, { loadedMiles })
	}

	async function closeIncident(incidentId: string, status: Units.UnitStatus, unitId?: string, reason?: string, turnoverCompany?: string) {
		const auth = useAuth();
		if (!auth.currentUID) throw new Error('not logged in!')

		const { data: res } = await updateIncidentStatus({
			companyId: getCompanyId(),
			incidentId, status, reason, unitId,
			changedAt: new Date().toISOString(),
			wasOffline: false,
			updateReqId: crypto.randomUUID(),
			source: `WEB ${VERSION}`
		});

		if (res === "DUPLICATE_REQUEST") {
			toast.simple("Duplicate status update was ignored");
		}

		if (unitId) unitIncident.value.delete(unitId);

		const incidentChanges: Partial<Incident.Incident> = {
			active: false,
		}
		if (turnoverCompany) incidentChanges.missedFlightTurnoverCompany = turnoverCompany;
		if (unitId) incidentChanges.assignedUnit = unitId;

		await updateIncident(incidentId, incidentChanges)
	}

	async function generateAndSendReport(incidentId: string) {
		const companyId = auth.companyId;
		if (!companyId) throw new Error("No companyId!!");

		await queueIncidentReport({ incidentId, companyId });
	}

	const activeIncidents = computed(() => [...incidents.value.values()].filter(val => val.active && val.assignedUnit));
	const pendingIncidents = computed(() => [...incidents.value.values()].filter(val => val.active && !val.assignedUnit));

	const amtIncidents = computed(() => [...incidents.value].filter(([_key, incident]) => !!incident.amtInfo && !incident.amtInfo.responded).map(([_key, val]) => val));

	async function amtRespond(incidentId: string) {
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`Incident ${incidentId} does not exist`);
		if (!incident.amtInfo) throw new Error(`Incident ${incident} does not have amt info`);

		const amtInfo = { ...incident.amtInfo!, responded: true };

		await updateIncident(incidentId, { amtInfo })
	}

	async function addPosition(incidentId: string, position: string) {
		const collection = getCollection();
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`Incident ${incidentId} does not exist`);
		await collection.updateDoc(incidentId, {
			positionLog: [
				...incident.positionLog || [],
				{
					position,
					timeStamp: new Date().toISOString(),
					user: auth.currentUID || '',
				}
			]
		})
	}

	async function deletePosition(incidentId: string, i: number) {
		const collection = getCollection();
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`Incident ${incidentId} does not exist`);
		await collection.updateDoc(incidentId, {
			positionLog: [
				...incident.positionLog.filter((_v, index) => index !== i)
			]
		});
	}

	async function addComment(incidentId: string, comment: string) {
		const collection = getCollection();
		const incident = incidents.value.get(incidentId);
		const user = users.currentUser
		if (!incident) throw new Error(`Incident ${incidentId} does not exist`);
		await collection.updateDoc(incidentId, {
			comments: [
				...incident.comments || [],
				{
					comment,
					timeStamp: new Date().toISOString(),
					createdBy: {
						id: auth.currentUID || '',
						name: users.currentUser?.name || { firstName: "Unknown", lastName: "Unknown", fullName: "Unknown" }
					}
				}
			]
		})
	}

	async function deleteComment(incidentId: string, i: number) {
		const collection = getCollection();
		const incident = incidents.value.get(incidentId);
		if (!incident) throw new Error(`Incident ${incidentId} does not exist`);
		await collection.updateDoc(incidentId, {
			comments: [
				...(incident.comments || []).filter((_v, index) => index !== i)
			]
		});
	}

	async function occRespond(incidentId: string, approved: boolean, signatureUrl: string) {
		const collection = getCollection();
		collection.updateDoc(incidentId, {
			occResponse: {
				approved,
				signatureUrl,
				timeStamp: new Date().toISOString(),
				createdBy: {
					id: auth.currentUID || '',
					name: users.currentUser!.name
				}
			}
		})
	}

	function removeIncidentChange(incidentId: string) {
		incidentChanges.value.delete(incidentId)
	}

	async function deleteIncident(incidentId: string) {
		const collection = getCollection();
		await collection.deleteDoc(incidentId);
	}

	async function addIncident(incident: Incident.Incident) {
		const collection = getCollection();
		const res = await collection.add(incident);
		return res;
	}

	return {
		incidents, incidentChanges, activeIncidents, pendingIncidents, loaded, waitForReady,
		updateIncident, updateStatus, closeIncident, removeIncidentChange, generateAndSendReport,
		unitIncident, amtIncidents, amtRespond, addPosition, addComment, occRespond,
		deleteComment, deletePosition, declineIncident, deleteIncident,
		getCollection, reopenIncident, overrideIncidentUnit, setLoadedMiles,
		unitUserAcks, addIncident
	}
}) 